import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api, { getApiErrorMessage } from '../../api';
import { useParticipantTheme } from '../../utils/theme';
import { getParticipantKey, useQaUpdates } from './useQa';
import { companyLogoUrl } from '../../utils/companyLogo';
import ShareCard from '../presentations/ShareCard';
import './Qa.css';

const MAX_LEN = 300;

const OWN_STATUS_NOTES = {
    checking: 'Проверяется…',
    pending: 'Ждёт одобрения ведущего',
    rejected: 'Не прошёл модерацию',
    hidden: 'Скрыт ведущим',
};

function QaParticipant() {
    const { sessionRef } = useParams();
    const participantKey = useMemo(() => getParticipantKey(), []);
    const [data, setData] = useState(null);
    useParticipantTheme(data?.settings?.theme);
    const [error, setError] = useState(null);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [notice, setNotice] = useState(null);
    const [sort, setSort] = useState('popular');
    const [showAnswered, setShowAnswered] = useState(false);
    const [votingId, setVotingId] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/public/qa/${sessionRef}`, { params: { viewer: participantKey } });
            setData(res.data);
            setError(null);
        } catch (err) {
            setError(err.response?.status === 404 ? 'Сессия вопросов не найдена' : getApiErrorMessage(err, 'Не удалось загрузить вопросы'));
        }
    }, [sessionRef, participantKey]);

    useEffect(() => { load(); }, [load]);
    useQaUpdates(data?.session?.public_id || sessionRef, load);

    useEffect(() => {
        if (data?.session?.title) document.title = `Вопросы — ${data.session.title}`;
    }, [data]);

    const submit = async (e) => {
        e.preventDefault();
        const value = text.trim();
        if (value.length < 3 || sending) return;
        setSending(true);
        setNotice(null);
        try {
            const res = await api.post(`/public/qa/${sessionRef}/questions`, { text: value, author_key: participantKey });
            setText('');
            const status = res.data.status;
            setNotice({
                type: status === 'rejected' ? 'error' : 'success',
                text: status === 'approved' ? 'Вопрос опубликован. Другие участники могут за него проголосовать.'
                    : status === 'checking' ? 'Вопрос отправлен и проходит автоматическую проверку.'
                    : status === 'pending' ? 'Вопрос отправлен. Он появится после одобрения ведущим.'
                    : `Вопрос не прошёл модерацию${res.data.moderation_reason ? `: ${res.data.moderation_reason}` : '.'}`,
            });
            load();
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось отправить вопрос') });
        } finally {
            setSending(false);
        }
    };

    const vote = async (question) => {
        if (votingId) return;
        setVotingId(question.id);
        // Оптимистичное обновление: список перечитается по событию с сервера
        setData((prev) => ({
            ...prev,
            questions: prev.questions.map((q) => (q.id === question.id
                ? { ...q, voted: !q.voted, votes: q.votes + (q.voted ? -1 : 1) }
                : q)),
        }));
        try {
            await api.post(`/public/qa/${sessionRef}/questions/${question.id}/vote`, { voter_key: participantKey });
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось проголосовать') });
            load();
        } finally {
            setVotingId(null);
        }
    };

    if (error) {
        return <div className="qa-page"><div className="qa-card qa-card--center"><h2>{error}</h2></div></div>;
    }
    if (!data) return <div className="qa-page"><div className="qa-loading">Загрузка…</div></div>;

    const { session, settings, questions } = data;
    const active = questions.filter((q) => q.status !== 'answered');
    const answered = questions.filter((q) => q.status === 'answered');
    const sorted = [...active].sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        if (sort === 'new') return new Date(b.created_at) - new Date(a.created_at);
        return b.votes - a.votes || new Date(a.created_at) - new Date(b.created_at);
    });

    return (
        <div className="qa-page">
            <header className="qa-header">
                {session.company_logo_path && (
                    <div className="qa-logo">
                        <img src={companyLogoUrl(session.company_logo_path)} alt="Логотип" />
                    </div>
                )}
                <div className="qa-header__label">Вопросы спикеру</div>
                <h1 className="qa-header__title">{session.title}</h1>
            </header>

            <main className="qa-main">
                {settings.enabled ? (
                    <form className="qa-card qa-ask" onSubmit={submit}>
                        <label htmlFor="qa-text" className="qa-ask__label">Ваш вопрос</label>
                        <textarea
                            id="qa-text"
                            className="qa-ask__input"
                            value={text}
                            maxLength={MAX_LEN}
                            rows={3}
                            placeholder="Например: как применить это в небольшой команде?"
                            onChange={(e) => setText(e.target.value)}
                        />
                        <div className="qa-ask__footer">
                            <span className="qa-ask__counter">{text.length} / {MAX_LEN} · анонимно</span>
                            <button type="submit" className="btn btn--primary" disabled={sending || text.trim().length < 3}>
                                {sending ? 'Отправка…' : 'Задать вопрос'}
                            </button>
                        </div>
                        {settings.premoderation && (
                            <p className="qa-hint">Вопросы появляются в списке после одобрения ведущим.</p>
                        )}
                    </form>
                ) : (
                    <div className="qa-card qa-closed">Приём вопросов сейчас закрыт.</div>
                )}

                {data.share?.enabled && (
                    <ShareCard kind="qa" refId={session.public_id} share={data.share} variant="collapsible" />
                )}

                {notice && (
                    <div className={`qa-notice qa-notice--${notice.type}`} role="status">
                        <span>{notice.text}</span>
                        <button type="button" onClick={() => setNotice(null)} aria-label="Скрыть">✕</button>
                    </div>
                )}

                <div className="qa-list-head">
                    <h2>Вопросы ({active.length})</h2>
                    <div className="qa-sort" role="group" aria-label="Сортировка">
                        <button type="button" className={sort === 'popular' ? 'is-active' : ''} onClick={() => setSort('popular')}>Популярные</button>
                        <button type="button" className={sort === 'new' ? 'is-active' : ''} onClick={() => setSort('new')}>Новые</button>
                    </div>
                </div>

                {sorted.length === 0 && (
                    <p className="qa-empty">Пока нет вопросов. Задайте первый!</p>
                )}

                <ul className="qa-list">
                    {sorted.map((q) => {
                        const canVote = q.status === 'approved';
                        return (
                            <li key={q.id} className={`qa-item${q.pinned ? ' qa-item--pinned' : ''}${q.mine ? ' qa-item--mine' : ''}`}>
                                <div className="qa-item__body">
                                    {q.pinned && <div className="qa-badge qa-badge--live">● Сейчас обсуждается</div>}
                                    <p className="qa-item__text">{q.text}</p>
                                    <div className="qa-item__meta">
                                        {q.mine && <span className="qa-badge">Ваш вопрос</span>}
                                        {q.mine && OWN_STATUS_NOTES[q.status] && (
                                            <span className={`qa-badge qa-badge--${q.status}`}>
                                                {OWN_STATUS_NOTES[q.status]}
                                                {q.status === 'rejected' && q.moderation_reason ? `: ${q.moderation_reason}` : ''}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {canVote && (
                                    <button
                                        type="button"
                                        className={`qa-vote${q.voted ? ' qa-vote--on' : ''}`}
                                        onClick={() => vote(q)}
                                        disabled={votingId === q.id}
                                        aria-pressed={q.voted}
                                        aria-label={q.voted ? 'Убрать голос' : 'Поддержать вопрос'}
                                    >
                                        <span aria-hidden>▲</span>
                                        <span>{q.votes}</span>
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>

                {answered.length > 0 && (
                    <div className="qa-answered">
                        <button type="button" className="qa-answered__toggle" onClick={() => setShowAnswered((v) => !v)} aria-expanded={showAnswered}>
                            {showAnswered ? '▾' : '▸'} Отвеченные ({answered.length})
                        </button>
                        {showAnswered && (
                            <ul className="qa-list qa-list--answered">
                                {answered.map((q) => (
                                    <li key={q.id} className="qa-item qa-item--answered">
                                        <div className="qa-item__body">
                                            <p className="qa-item__text">{q.text}</p>
                                            <div className="qa-item__meta"><span className="qa-badge qa-badge--answered">✓ Отвечен</span></div>
                                        </div>
                                        <span className="qa-vote qa-vote--static"><span aria-hidden>▲</span><span>{q.votes}</span></span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}

export default QaParticipant;
